import { FC, ReactNode, useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { getAuth, sendEmailVerification, signOut } from "firebase/auth";
import {
  Box,
  Menu,
  Text,
  Anchor,
  Container,
  ActionIcon,
  createStyles,
} from "@mantine/core";
import { RxHamburgerMenu } from "react-icons/rx";

import { Logo } from "@components/Logo/Logo";
import { LanguageSelector } from "@components/LanguageSelector/LanguageSelector";

import { AuthContext } from "@components/AuthProvider/contexts/Auth.context";

const useStyles = createStyles((t) => ({
  content: {
    display: "flex",
    flexDirection: "column",
  },
  banner: {
    color: "white",
    display: "flex",
    fontSize: "14px",
    textAlign: "center",
    alignItems: "center",
    padding: "0.5rem 1rem",
    justifyContent: "center",
    background: t.primaryColor,
  },
  headerWrapper: {
    width: "100%",
    background: "white",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  nav: {
    display: "flex",
    gap: t.spacing.lg,
    alignItems: "center",

    [`@media (max-width: ${t.breakpoints.md}px)`]: {
      display: "none",
    },
  },
  burger: {
    display: "none",
    [`@media (max-width: ${t.breakpoints.md}px)`]: {
      display: "unset",
    },
  },
  inactive: {
    color: `${t.colors.gray[6]} !important`,
  },
}));

export const Header: FC<{ rightSection?: ReactNode }> = ({ rightSection }) => {
  const [searchParams] = useSearchParams();
  const [hide, setHide] = useState(searchParams.get("verified") === null);

  const { user, authUser } = useContext(AuthContext)!;

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { classes } = useStyles();

  const { pathname } = useLocation();

  useEffect(() => {
    setTimeout(() => {
      setHide(true);
    }, 3_000);
  });

  return (
    <Box className={classes.content}>
      <Box className={classes.headerWrapper}>
        <Container style={{ width: "100%" }} p="lg">
          <Box className={classes.header}>
            <Logo />
            <Box>
              <Box className={classes.burger}>
                <Menu position="bottom-end" withArrow>
                  <Menu.Target>
                    <ActionIcon>
                      <RxHamburgerMenu />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {user.isAdmin && (
                      <>
                        <Menu.Item
                          onClick={() => navigate("/submissions")}
                          className={`${
                            pathname !== "/submissions" && classes.inactive
                          }`}
                        >
                          {t("Submissions")}
                        </Menu.Item>
                        <Menu.Item
                          onClick={() => navigate("/users")}
                          className={`${
                            pathname !== "/users" && classes.inactive
                          }`}
                        >
                          {t("Users")}
                        </Menu.Item>
                      </>
                    )}
                    <Menu.Item
                      onClick={() => navigate("/portal")}
                      className={`${
                        pathname !== "/portal" && classes.inactive
                      }`}
                    >
                      {t("Forms")}
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => navigate("/settings")}
                      className={`${
                        pathname !== "/settings" && classes.inactive
                      }`}
                    >
                      {t("Account")}
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => {
                        signOut(getAuth());
                        navigate("/login");
                      }}
                      className={classes.inactive}
                    >
                      {t("Logout")}
                    </Menu.Item>
                    <Menu.Label>
                      <LanguageSelector />
                    </Menu.Label>
                  </Menu.Dropdown>
                </Menu>
              </Box>
              <Box className={classes.nav}>
                {user.isAdmin && (
                  <>
                    <Anchor
                      onClick={() => navigate("/submissions")}
                      className={`${
                        pathname !== "/submissions" && classes.inactive
                      }`}
                    >
                      {t("Submissions")}
                    </Anchor>
                    <Anchor
                      onClick={() => navigate("/users")}
                      className={`${pathname !== "/users" && classes.inactive}`}
                    >
                      {t("Users")}
                    </Anchor>
                  </>
                )}
                <Anchor
                  onClick={() => navigate("/portal")}
                  className={`${pathname !== "/portal" && classes.inactive}`}
                >
                  {t("Forms")}
                </Anchor>
                <Anchor
                  onClick={() => navigate("/settings")}
                  className={`${pathname !== "/settings" && classes.inactive}`}
                >
                  {t("Account")}
                </Anchor>
                <Anchor
                  onClick={() => {
                    signOut(getAuth());
                    navigate("/login");
                  }}
                  className={classes.inactive}
                >
                  {t("Logout")}
                </Anchor>
                <LanguageSelector />
                {rightSection}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      {!authUser.emailVerified ? (
        <Box className={classes.banner}>
          <Container>
            {t("accountUnverified")}{" "}
            <Text
              color="blue"
              sx={{
                textDecoration: "underline",
                cursor: "pointer",
                display: "inline",
              }}
              onClick={async () => {
                await sendEmailVerification(authUser);
              }}
            >
              {t("sendVerificationEmail")}
            </Text>
          </Container>
        </Box>
      ) : hide ? (
        <></>
      ) : (
        <Box className={classes.banner} style={{ background: "green" }}>
          <Container>{t("accountVerified")}</Container>
        </Box>
      )}
    </Box>
  );
};
